import {
    Badge,
    Box,
    Button,
    FileInput,
    Group,
    Modal,
    NumberInput,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import {
    genericValidator,
    validateICCIDForMantine,
    validateIMEIForMantine,
} from '../../../../../utils/validation';
import {
    getMobileDevices,
    getUserApiKeys,
    getUsers,
} from '../../../../../http/apiClient';
import { useEffect, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MOBILE_CARRIERS } from '../../../../common/enums/enums';
import { SearchableSelect } from '../../../../common';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { hasRole } from '../../../../../utils/roles';
import { toGroupedOptions } from '../../../../../utils/enumUtils';
import useAddSimCard from '../../../../../http/mutations/resources/useAddSimCard';
import useAddSimCardBatch from '../../../../../http/mutations/resources/useAddSimCardBatch';
import { useAuthStore } from '../../../../../store';
import { useForm } from '@mantine/form';

const AddSimCardModal = ({ opened, close }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const carrierOptions = toGroupedOptions(MOBILE_CARRIERS);

    const getInitialValues = () => ({
        iccids: '',
        imeis: '',
        numbers: '',
        mobileDeviceId: '',
        carrier: 'USMobile',
        upstreamCarrier: 'Verizon',
        ownerId: hasRole(userProfile, 'SUPER_ADMIN')
            ? userProfile?.id.toString()
            : null,

        email: '',
        password: '',
        activationCost: '0.00',
    });

    const form = useForm({
        initialValues: getInitialValues(),
        validateInputOnBlur: true,
        validate: {
            iccids: (values) => {
                const transformed = form.getTransformedValues({
                    iccids: values,
                });
                if (transformed.iccids.length === 0)
                    return 'At least one iccid is required';

                for (const i in transformed.iccids) {
                    const value = transformed.iccids[i];
                    const error = validateICCIDForMantine(value);
                    if (error) {
                        return error;
                    }
                }
            },
            imeis: (values) => {
                const transformed = form.getTransformedValues({
                    imeis: values,
                });
                for (const i in transformed.imeis) {
                    const value = transformed.imeis[i];
                    const error = validateIMEIForMantine(value);
                    if (error) {
                        return error;
                    }
                }
            },
            numbers: (values) => {
                const transformed = form.getTransformedValues({
                    numbers: values,
                });
                if (transformed.numbers.length === 0)
                    return 'Numbers are required';

                for (const i in transformed.numbers) {
                    const value = transformed.numbers[i];
                    if (value.length < 10) {
                        return 'Invalid number';
                    }
                }
            },
            ownerId: (value) => {
                const { message } = genericValidator('User ID', value, {
                    required: hasRole(userProfile, 'SUPER_ADMIN'),
                });
                return message;
            },
            mobileDeviceId: (value) => {
                const { message } = genericValidator('Mobile device', value, {
                    required: true,
                });
                return message;
            },
        },
        transformValues: (values) => ({
            ...values,
            iccids:
                values.iccids
                    ?.split('\n')
                    .map((x) => x.trim())
                    .filter((x) => x) || [],
            imeis:
                values.imeis
                    ?.split('\n')
                    .map((x) => x.trim())
                    .filter((x) => x) || [],
            numbers:
                values.numbers
                    ?.split('\n')
                    .map((x) => x.trim())
                    .filter((x) => x) || [],
            mobileDeviceId: Number(values.mobileDeviceId),
        }),
    });

    useEffect(() => {
        form.setInitialValues(getInitialValues());
        form.reset();
    }, [opened]);

    const { isPending, mutate: addOrUpdate } = useAddSimCardBatch();

    const onSubmit = (model) => {
        /*console.log(model);
        return;*/

        addOrUpdate(model, {
            onSuccess(data, variables, context) {
                close();
            },
            onError(error, variables, context) {
                // setError(error);
            },
        });
    };

    return (
        <Modal.Root
            opened={opened}
            onClose={close}
            size={'md'}>
            <Modal.Overlay />
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>Add Sim Card</Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={form.onSubmit(onSubmit)}>
                        <Stack gap={'xs'}>
                            <Textarea
                                label={'Iccids'}
                                placeholder='Enter iccid(s)'
                                withAsterisk
                                autosize
                                minRows={1}
                                key={form.key('iccids')}
                                {...form.getInputProps('iccids')}
                            />

                            <Textarea
                                label={'Numbers'}
                                placeholder='Enter numbers(s)'
                                withAsterisk
                                autosize
                                minRows={1}
                                key={form.key('numbers')}
                                {...form.getInputProps('numbers')}
                            />

                            <Textarea
                                label={'Imeis'}
                                placeholder='Enter imeis'
                                withAsterisk
                                autosize
                                minRows={1}
                                key={form.key('imeis')}
                                {...form.getInputProps('imeis')}
                            />

                            <SearchableSelect
                                label={'Mobile Device'}
                                withAsterisk
                                placeholder='Mobile Device'
                                queryHttpFn={getMobileDevices}
                                queryKey={'mobile_devices'}
                                optionsMapFn={(x) => ({
                                    label: `${x.model} - ${x.imeiPrefix}`,
                                    value: x.id.toString(),
                                })}
                                /*defaultValue={{
                                    label: 'Random Cloudflare',
                                    value: '',
                                }}*/
                                key={form.key('mobileDeviceId')}
                                {...form.getInputProps('mobileDeviceId')}
                            />

                            <Group
                                grow
                                w={'100%'}>
                                <Select
                                    label={'Carrier'}
                                    placeholder='Select carrier'
                                    data={carrierOptions}
                                    key={form.key('carrier')}
                                    {...form.getInputProps('carrier')}
                                />
                                <Select
                                    label={'Upstream Carrier'}
                                    placeholder='Select upstream carrier'
                                    data={carrierOptions}
                                    key={form.key('upstreamCarrier')}
                                    {...form.getInputProps('upstreamCarrier')}
                                />
                            </Group>

                            {hasRole(userProfile, 'SUPER_ADMIN') ? (
                                <SearchableSelect
                                    label={'Owner'}
                                    withAsterisk
                                    placeholder='Owner'
                                    queryHttpFn={getUsers}
                                    queryKey={'users'}
                                    optionsMapFn={(user) => ({
                                        label: user.username,
                                        value: user.id.toString(),
                                    })}
                                    defaultValue={{
                                        label: userProfile?.username,
                                        value: userProfile?.id.toString(),
                                    }}
                                    key={form.key('ownerId')}
                                    {...form.getInputProps('ownerId')}
                                />
                            ) : null}

                            <Group
                                grow
                                w={'100%'}>
                                <TextInput
                                    label={'Email'}
                                    placeholder='Enter email/username'
                                    key={form.key('email')}
                                    {...form.getInputProps('email')}
                                />
                                <TextInput
                                    label={'Password'}
                                    placeholder='Enter password'
                                    key={form.key('password')}
                                    {...form.getInputProps('password')}
                                />
                            </Group>

                            <NumberInput
                                label='Activation Cost'
                                placeholder={'Enter activation cost'}
                                leftSection={'$'}
                                size={'xs'}
                                key={form.key('activationCost')}
                                {...form.getInputProps('activationCost')}
                            />

                            <Group justify='flex-end'>
                                <Button
                                    type={'submit'}
                                    disabled={isPending || !form.isValid()}
                                    rightSection={
                                        isPending ? (
                                            <FontAwesomeIcon
                                                icon={faSpinnerThird}
                                                spin
                                            />
                                        ) : null
                                    }>
                                    Create
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};
export default AddSimCardModal;
